/* eslint-disable camelcase */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import Add from '@mui/icons-material/Add';

import { registerField } from 'core/forms';
import { useModals } from 'core/notifications';

import { Button } from 'uikit/buttons';

import CatalogActivityListItem from '@/exceed/components/catalogActivityListItem';
import FileListItem from '@/exceed/components/fileListItem';

import CatalogActivityImg from '../assets/catalog-activity.png';
import FileUploadImg from '../assets/file-upload.png';
import GoogleDriveImg from '../assets/google-drive.png';
import OneDriveImg from '../assets/one-drive.png';

import AddSourceMaterialModal from './addSourceMaterialModal';
import getSourceMaterialSchema from '../schemas/getSourceMaterialSchema';

import styles from '../styles/formFieldSourceMaterial.module.scss';

const DEFAULT_MODEL = { source_content_type: 'catalog_activity' };

const IMAGE_MAP = {
  catalog_activity: {
    src: CatalogActivityImg,
    alt: i18n.t('source-material.catalog-activity-alt'),
  },
  file_upload: {
    src: FileUploadImg,
    alt: i18n.t('source-material.file-upload-alt'),
  },
  google_drive: {
    src: GoogleDriveImg,
    alt: i18n.t('source-material.google-drive-alt'),
  },
  one_drive: {
    src: OneDriveImg,
    alt: i18n.t('source-material.one-drive-alt'),
  },
};

const getModel = (schema, value) => {
  const model = { ...DEFAULT_MODEL };

  Object.keys(schema).forEach((key) => {
    if (model[key]) return;

    if (key === 'catalog_activity') {
      model[key] = value.filter(({ material_type }) => material_type === 'Course');
    } else {
      model[key] = value.filter(({ import_type }) => import_type === key);
    }
  });

  return model;
};

const SourceMaterial = ({ fieldId, schema, updateField, value = [] }) => {
  const isEmptyValue = !value || value.length === 0;
  const { addModal } = useModals();
  const { hideParentModal, showParentModal } = schema;
  const sourceMaterialSchema = getSourceMaterialSchema(schema);

  const onUpdate = (payload) => {
    let update = [];

    Object.keys(payload).forEach((key) => {
      if (Array.isArray(payload[key])) {
        update = [...update, ...payload[key]];
      }
    });

    update = sortBy(update, ['name']);

    updateField(update);
  };

  const onRemove = (removeFile) => {
    const newValue = value.filter((file) => !isEqual(file, removeFile));
    updateField(newValue);
  };

  const onClick = async () => {
    const id = 'new-source-material';

    if (hideParentModal) hideParentModal();

    const { type } = await addModal({
      className: styles.modal,
      component: (
        <AddSourceMaterialModal
          model={getModel(sourceMaterialSchema, value)}
          onSubmit={onUpdate}
          schema={sourceMaterialSchema}
        />
      ),
      hasCloseButton: true,
      id,
      isFullScreen: true,
    });

    if (type === 'CLOSE' && showParentModal) {
      showParentModal();
    }
  };

  const renderListItems = () =>
    value.map((item, index) => {
      const { material_type, status } = item;

      if (material_type !== 'Resource') {
        return (
          <CatalogActivityListItem
            // eslint-disable-next-line react/no-array-index-key
            key={`source_material_${index}`}
            defaultImage={IMAGE_MAP.catalog_activity}
            id={item.material_id}
            onRemove={() => onRemove(item)}
            status={status}
            showImage
          />
        );
      }

      const { name, file_size } = item;

      return (
        <FileListItem
          // eslint-disable-next-line react/no-array-index-key
          key={`source_material_${index}`}
          name={name}
          fileSize={file_size}
          materialType={material_type}
          image={IMAGE_MAP[item.import_type]}
          onRemove={() => onRemove(item)}
          status={status}
        />
      );
    });

  return (
    <div className={styles.element} id={fieldId}>
      {!isEmptyValue && <ul className={styles.list}>{renderListItems()}</ul>}
      <Button variant="outlined" onClick={onClick}>
        <Add className={styles.icon} sx={{ height: 18, width: 18 }} />
        {i18n.t('source-material.add')}
      </Button>
    </div>
  );
};

SourceMaterial.propTypes = {
  fieldId: PropTypes.string.isRequired,
  schema: PropTypes.shape({
    catalogActivitySchema: PropTypes.shape({
      loadOptions: PropTypes.func.isRequired,
      sortSelectedOptionsBy: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    }),
    googleDriveSchema: PropTypes.shape({
      mimeTypes: PropTypes.arrayOf(PropTypes.string),
    }),
    hideParentModal: PropTypes.func,
    showParentModal: PropTypes.func,
  }).isRequired,
  updateField: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      material_type: PropTypes.oneOf(['Course', 'Resource']).isRequired,
      material_id: PropTypes.number,
      import_type: PropTypes.oneOf(['file_upload', 'google_drive', 'one_drive']),
      file_size: PropTypes.number,
      name: PropTypes.string,
      resource: PropTypes.shape({
        external_id: PropTypes.string,
        mime: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        metadata: PropTypes.shape({}),
      }),
      status: PropTypes.oneOf([
        'created',
        'failure',
        'rag_token_requested',
        'request_file_transcript',
        'requested',
        'success',
      ]).isRequired,
    }),
  ),
};

registerField('SourceMaterial', memo(SourceMaterial));

export default SourceMaterial;
